import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmModal from './ConfirmModal';
import { accountApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AI_CONSENT_EVENT } from '../native/aiConsent';

/**
 * Asks for the AI permission when the backend needs it (428 ai_consent_required).
 * Names who gets what, as App Store guideline 5.1.2(i) asks; withdrawn in
 * Settings -> Data & privacy.
 */
export default function AiConsentDialog() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  // The waiting request's answer. A ref, not state: the dialog's own close runs right after
  // a button, and only the first of the two may answer (else "Allow" would also decline).
  const pending = useRef(null);

  useEffect(() => {
    const ask = (e) => {
      pending.current = e.detail.resolve;
      setOpen(true);
    };
    window.addEventListener(AI_CONSENT_EVENT, ask);
    return () => window.removeEventListener(AI_CONSENT_EVENT, ask);
  }, []);

  const finish = useCallback(async (granted) => {
    const resolve = pending.current;
    if (!resolve) return;
    pending.current = null;
    setOpen(false);
    if (granted) {
      try {
        await accountApi.setAiConsent(true);
        refreshUser?.();
      } catch {
        resolve(false);
        return;
      }
    }
    resolve(granted);
  }, [refreshUser]);

  return (
    <ConfirmModal
      isOpen={open}
      onClose={() => finish(false)}
      onConfirm={() => finish(true)}
      title={t('ai_consent.title')}
      message={(
        <span className="block space-y-2">
          <span className="block">{t('ai_consent.what')}</span>
          <span className="block">{t('ai_consent.who')}</span>
          <span className="block">{t('ai_consent.withdraw')}</span>
        </span>
      )}
      confirmLabel={t('ai_consent.agree')}
    />
  );
}
